import { Middleware } from 'redux';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { buildFromConfig } from '@makemydeal/dr-common-utils';

import { bootstrapActionTypes, dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import {
    seedCustomerCard,
    configureCustomerCard,
    init,
    checkCustomerSpdRSAA,
    SAVE_PARTICIPANTS_REQUEST,
    SAVE_PARTICIPANTS_SUCCESS,
    SAVE_PARTICIPANTS_FAILURE,
    SWAP_BUYER_COBUYER_SUCCESS,
    DROP_CO_BUYER_SUCCESS,
    START_OPERATION,
    CARD_OPERATION_KIND
} from '@makemydeal/dr-activities-customer-card';
import { swappedBuyerCoBuyer, clearCoBuyer } from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as customerSpdActionCreators from '@makemydeal/dr-dash-store/actions/CustomerSpdActionCreators';
import * as creditAppActionCreators from '@makemydeal/dr-dash-store/actions/creditAppActionCreators';
import { ecommerceOrderCleared } from '@makemydeal/dr-dash-store/actions/orderActionCreators';
import { StateTree } from '@makemydeal/dr-dash-types';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as editCustomerSelectors from '@makemydeal/dr-dash-store/selectors/editCustomerSelectors';
import { SHOW_EDIT_CUSTOMER_SLIDEOUT } from '@makemydeal/dr-dash-store/actionTypes/editCustomerActionTypes';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';
// The activity owns the SPD lifecycle: its own middleware watches SEED_CUSTOMER_CARD and, when the
// seed carries prefetchSpd, fires the SPD presence CHECK itself — the host never dispatches
// checkCustomerSpdRSAA directly. Registered as its own chain entry in remainingMiddleware.ts.
export const customerCardActivityMiddleware = init().middlewareMgr.middleware;

export const customerCardMiddleware: Middleware<unknown, StateTree> = (store: any) => (next: any) => (action: AnyFSA) => {
    switch (action.type) {
        case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
            const state = store.getState();
            const bffBaseUrl = buildFromConfig(configSelectors.getServicesBff(state));

            // Configure the activity with the (static) bff base url.
            configureCustomerCard({ bffBaseUrl, hostApp: 'MV' });

            // Seed the customer card store with deal customer data
            const buyer = offerReduxSelectors.getShopperInfo(state);
            const coBuyer = offerReduxSelectors.getCoBuyerInfo(state) || null;
            const hasCoBuyer = offerReduxSelectors.hasCoBuyer(state);
            const vehicleInsuranceInfo = baseDealSelectors.getVehicleInsuranceInfo(state);
            const isVehicleInsuranceUpdated = baseDealSelectors.isVehicleInsuranceUpdated(state) ?? false;
            const contractApiErroredVehicleInsurance = contractingSelectors.getContractApiErroredVehicleInsurance(state);
            const dealId = offerReduxSelectors.getDealXgId(state);
            const dealerId = dealerSelectors.getDealerId(state);
            const commonOrgId = offerReduxSelectors.getCommonOrgId(state);
            const isDealSoldOrContracted = editCustomerSelectors.getIsDealSoldOrContracted(state);

            store.dispatch(
                seedCustomerCard({
                    buyer,
                    coBuyer,
                    hasCoBuyer,
                    initialTab: 'buyer',
                    vehicleInsuranceInfo,
                    isVehicleInsuranceUpdated,
                    contractApiErroredVehicleInsurance,
                    dealId,
                    dealerId,
                    prefetchSpd: true,
                    isDealSoldOrContracted,
                    isContractGenerated: contractingSelectors.getIsContractGenerated(state),
                    commonOrgId
                })
            );

            break;
        }
        case SHOW_EDIT_CUSTOMER_SLIDEOUT: {
            // Only the activities card reads the store this seeds, so skip the work for the other two
            // card variants the slide-out can host.
            const state = store.getState();
            const isActivitiesCustomerCardEnabled = featureToggleSelectors.isActivitiesCustomerCardEnabled(state);
            // Reseed only when the card was actually closed ('' means nothing is open). This action also
            // fires while a card is already open — the co-buyer link dispatches it without closing the
            // buyer first, and the panel stays mounted — and reseeding then would overwrite whatever the
            // user has typed but not yet saved. Read before next(action), which is what updates it.
            const wasClosed = editCustomerSelectors.showEditCustomerSlideout(state) === '';
            if (isActivitiesCustomerCardEnabled && wasClosed) {
                const buyer = offerReduxSelectors.getShopperInfo(state);
                const coBuyer = offerReduxSelectors.getCoBuyerInfo(state) || null;
                const hasCoBuyer = offerReduxSelectors.hasCoBuyer(state);
                const vehicleInsuranceInfo = baseDealSelectors.getVehicleInsuranceInfo(state);
                const isVehicleInsuranceUpdated = baseDealSelectors.isVehicleInsuranceUpdated(state) ?? false;
                const contractApiErroredVehicleInsurance = contractingSelectors.getContractApiErroredVehicleInsurance(state);
                const isDealSoldOrContracted = editCustomerSelectors.getIsDealSoldOrContracted(state);
                // A quote has no DealXG record until its first save, and nothing else refreshes these —
                // seeded only at bootstrap, the card kept the empty values until a page reload.
                const dealId = offerReduxSelectors.getDealXgId(state);
                const commonOrgId = offerReduxSelectors.getCommonOrgId(state);

                store.dispatch(
                    seedCustomerCard({
                        buyer,
                        coBuyer,
                        hasCoBuyer,
                        vehicleInsuranceInfo,
                        isVehicleInsuranceUpdated,
                        contractApiErroredVehicleInsurance,
                        prefetchSpd: false,
                        isDealSoldOrContracted,
                        isContractGenerated: contractingSelectors.getIsContractGenerated(state),
                        dealId,
                        commonOrgId
                    })
                );
            }

            break;
        }
        case SAVE_PARTICIPANTS_REQUEST: {
            if (featureToggleSelectors.isCreditAppUpdateOnChangeBuyerEnabled(store.getState())) {
                store.dispatch(creditAppActionCreators.participantsSaveStarted());
            }

            break;
        }
        case SAVE_PARTICIPANTS_SUCCESS: {
            if (featureToggleSelectors.isCreditAppUpdateOnChangeBuyerEnabled(store.getState())) {
                const creditApplicationId = action.payload?.data?.creditApplicationId;
                store.dispatch(
                    creditApplicationId
                        ? creditAppActionCreators.creditAppCopiedByParticipantsSave(creditApplicationId)
                        : creditAppActionCreators.participantsSaveFinishedWithoutCopy()
                );
            }
            if (action.payload?.data?.ecommerceOrderCancellation?.result === 'cancelled') {
                store.dispatch(ecommerceOrderCleared());
            }

            break;
        }
        case SAVE_PARTICIPANTS_FAILURE: {
            if (featureToggleSelectors.isCreditAppUpdateOnChangeBuyerEnabled(store.getState())) {
                store.dispatch(creditAppActionCreators.participantsSaveFinishedWithoutCopy());
            }

            break;
        }
        case SWAP_BUYER_COBUYER_SUCCESS: {
            store.dispatch(swappedBuyerCoBuyer(action.payload.shopper, action.payload.coBuyer));

            break;
        }
        // The card's thunk owns the server save; these are the local effects the legacy
        // CoBuyerRemoveModal dispatched inline. The ccid has to be read before clearCoBuyer().
        case DROP_CO_BUYER_SUCCESS: {
            const state = store.getState();
            const coBuyerCommonConsumerId = offerReduxSelectors.getCoBuyerCommonConsumerId(state);

            store.dispatch(clearCoBuyer());
            if (dealerSelectors.isSpdManagementEnabled(state)) {
                store.dispatch(customerSpdActionCreators.removeCoBuyerSpd(coBuyerCommonConsumerId));
            }

            break;
        }
        // Opens the slideout Add Customer needs, before next(action): SHOW_EDIT_CUSTOMER_SLIDEOUT
        // re-seeds the card and the seed clears `operation`, so the operation has to land last.
        case START_OPERATION: {
            if (action.payload === CARD_OPERATION_KIND.ADD_CUSTOMER) {
                store.dispatch(editCustomerActionCreators.showEditBuyerSlideout());
            } else if (action.payload === CARD_OPERATION_KIND.ADD_CO_BUYER) {
                store.dispatch(editCustomerActionCreators.showEditCoBuyerSlideout());
            }

            break;
        }
        case offerReduxActionTypes.UPDATED_COBUYER: {
            const state = store.getState();
            const commonConsumerId = action.payload?.commonConsumerId;
            if (commonConsumerId && dealerSelectors.isSpdManagementEnabled(state)) {
                store.dispatch(checkCustomerSpdRSAA(commonConsumerId) as never);
            }

            break;
        }
    }

    return next(action);
};
